/**
 * 왕복 파형 — Campus 굽는 스크립트(`model-anim-glb.mjs` 의 `wave`)와 <b>같은 식</b>(SR-SIM-09).
 *
 * <p>작가는 어떤 관절을 시간의 사인으로 그렸다(`swing`). 그런데 그 사인이 사실은 크랭크를 따라가는 것이면
 * (워킹맨 발판: "크랭크와 같은 주기의 사인"), 시뮬레이터는 시간이 아니라 <b>크랭크 각</b>으로 읽어야 한다 —
 * 모터가 멈추면 발도 멈추고, 거꾸로 돌면 거꾸로 흔들려야 한다. 그 읽기를 여기서 한다({@link cycleAngle}).
 * <p>식을 새로 짓지 않고 굽는 쪽 것을 그대로 옮긴다: 둘이 다르면 구운 키프레임과 시뮬레이터가 어긋난다.
 * 여기 있는 것은 산수뿐이다.
 */
import { cos, sin } from "./det-math";   // 결정론 수학 — 엔진마다 끝자리가 갈리지 않게(FIELD-CRQ-001 D-4)

/** 한 구간 — 도(swing) 또는 cm(travel). period 를 안 적으면 클립 길이다 */
export type WaveSpec = { from: number; to: number; period?: number; start?: string; delay?: number; cycles?: number };

/** 굽는 쪽 `wave1` — start:"mid"(기본, t=0 은 중립 · 사인) · "from"(t=0 은 from 끝 · 코사인 완급) */
function wave1({ from, to, period, start = "mid", delay = 0, cycles = Infinity }: WaveSpec, clip: number) {
  const P = period ?? clip;
  const mid = (from + to) / 2, amp = (to - from) / 2, end = delay + cycles * P;
  const g = start === "from"
    ? (u: number) => from + (to - from) * (1 - cos(2 * Math.PI * u)) / 2
    : (u: number) => mid + amp * sin(2 * Math.PI * u);
  return (t: number) => (t < delay ? g(0) : t >= end ? g(cycles) : g((t - delay) / P));
}

/** 굽는 쪽 `wave` — 배열이면 구간을 이어 붙인다 */
export function makeWave(spec: WaveSpec | WaveSpec[], clip: number): (t: number) => number {
  if (Array.isArray(spec)) {
    const ws = spec.map((s) => wave1(s, clip)), froms = spec.map((s) => s.from);
    return (t) => ws.reduce((acc, w, i) => acc + (w(t) - (froms[i] as number)), froms[0] as number);
  }
  return wave1(spec, clip);
}

/**
 * 크랭크가 <b>한 바퀴 도는 시간</b>(1/spin 초)마다 되풀이되는 파형인가 — 그래야 크랭크 각으로 읽을 수 있다.
 * <p>구간이 하나이고, 지연·횟수 제한이 없고, 주기가 한 바퀴 시간을 나누어떨어뜨려야 한다. 아니면 시간에 매인
 * 연출(버튼을 누른 뒤 한 번만 도는 것 등)이라 크랭크 각으로 바꾸면 거짓말이 된다.
 */
export function cyclesWithCrank(spec: unknown, spin: number, clip: number): spec is WaveSpec {
  if (!spec || typeof spec !== "object" || Array.isArray(spec) || !Number.isFinite(spin) || spin === 0) return false;
  const s = spec as WaveSpec;
  if (!Number.isFinite(s.from) || !Number.isFinite(s.to)) return false;
  if ((s.delay ?? 0) !== 0 || (s.cycles ?? Infinity) !== Infinity) return false;
  const P = s.period ?? clip, rev = 1 / Math.abs(spin);
  if (!(P > 0)) return false;
  const k = rev / P;
  return Math.abs(k - Math.round(k)) < 1e-6 && Math.round(k) >= 1;
}

/**
 * 크랭크 각(도)에서 이 관절의 각 — 크랭크가 θ 만큼 돈 것은 굽는 클립에서 θ/(360·spin) 초가 지난 것과 같다.
 * <p>한 바퀴로 접어서 읽는다: 모터를 거꾸로 돌려 θ 가 음수여도 파형은 제자리에서 되풀이된다.
 */
export function cycleAngle(wave: (t: number) => number, crankDeg: number, spin: number): number {
  const rev = 1 / Math.abs(spin);
  const u = crankDeg / (360 * spin);
  return wave(((u % rev) + rev) % rev);
}
